import { useEffect } from 'react';

import { OrientationEnum, PaperFormatEnum } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';

import {
  PRINTER_ORIENTATION_LANDSCAPE_CLASSNAME,
  PRINTER_ORIENTATION_PORTRAIT_CLASSNAME,
  PRINTER_FORMAT_A4_CLASSNAME,
  PRINTER_FORMAT_A3_CLASSNAME,
  PRINTER_SECTION_CLASSNAME,
} from '../../../GlobalStyles/utils/get-socna-printer-styles.util';
import { HtmlViewer } from '../../../HtmlViewer';

import { useDocumentResolverContext } from '../../contexts/DocumentResolverContext/hooks/useDocumentResolverContext.hook';
import { useContentGenerator } from '../../hooks/useContentGenerator.hook';
import type { SectionToPrint } from '../../types/print-group.type';

import { Title } from './Title';
import { PageBreak } from './PageBreak';

interface Props {
  readonly paperFormat: PaperFormatEnum;
  readonly isLast: boolean;
  readonly onGenerated: () => void;
  readonly orientation: OrientationEnum;
  readonly section: SectionToPrint;
}

export const Section = ({
  paperFormat,
  isLast,
  onGenerated: handleGenerated,
  orientation,
  section,
}: Readonly<Props>) => {
  const { printerSettings } = useDocumentResolverContext();

  const { data: titleGenerationData, isSuccess: titleGenerationIsSuccess } =
    useContentGenerator({
      template: section.title ?? EMPTY_STRING,
    });

  const { data: contentGenerationData, isSuccess: contentGenerationIsSuccess } =
    useContentGenerator({
      template: section.content ?? EMPTY_STRING,
    });

  useEffect(() => {
    if (!titleGenerationIsSuccess || !contentGenerationIsSuccess) {
      return;
    }

    handleGenerated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentGenerationIsSuccess, titleGenerationIsSuccess]);

  if (!titleGenerationIsSuccess || !contentGenerationIsSuccess) {
    return null;
  }

  return (
    <div
      className={`${PRINTER_SECTION_CLASSNAME} ${
        orientation === OrientationEnum.Landscape
          ? PRINTER_ORIENTATION_LANDSCAPE_CLASSNAME
          : PRINTER_ORIENTATION_PORTRAIT_CLASSNAME
      } ${
        paperFormat === PaperFormatEnum.A3
          ? PRINTER_FORMAT_A3_CLASSNAME
          : PRINTER_FORMAT_A4_CLASSNAME
      }`}
      key={section.id}
    >
      <Title
        displayPath={section.displayPath}
        title={titleGenerationData?.generated}
        useSeparator={
          !!printerSettings?.needsPrintFeedbacks && printerSettings.step === '0'
        }
      />
      <HtmlViewer
        html={contentGenerationData?.generated}
        sx={{ border: 'none', padding: 0 }}
      />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!section.hasPagebreakAfter && !isLast && <PageBreak />}
    </div>
  );
};
